import React, { useState, useEffect } from "react";
import { 
    Card, Table, Row, Col, Badge, Spinner, Alert, Button,
    Form, Modal, ProgressBar, OverlayTrigger, Tooltip
} from "react-bootstrap";
import { useParams, useNavigate, Link } from "react-router-dom";
import { formatDate, formatCurrency } from "../utils/formatters";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";

export default function InventaireDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [inventaire, setInventaire] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showValidateModal, setShowValidateModal] = useState(false);
    const [isValidating, setIsValidating] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [isCancelling, setIsCancelling] = useState(false);

    useEffect(() => {
        fetchInventaire();
    }, [id]);

    const fetchInventaire = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `https://sawd.julesferry-ga.com/api/inventaires.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setInventaire(result.data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleValider = async () => {
        setIsValidating(true);
        
        try {
            const token = localStorage.getItem("token");
            const userStr = localStorage.getItem("user");
            const user = userStr ? JSON.parse(userStr) : null;
            
            const response = await fetch('https://sawd.julesferry-ga.com/api/inventaires.php', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    action: 'valider',
                    id: id,
                    id_utilisateur: user?.id || user?.id_utilisateur || 1
                })
            });
            
            const result = await response.json();
            
            if (result.success) {
                setShowValidateModal(false);
                fetchInventaire();
            } else {
                alert(result.message || "Erreur lors de la validation");
            }
        } catch (err) {
            alert("Erreur de connexion au serveur");
        } finally {
            setIsValidating(false);
        }
    };

    const handleAnnuler = async () => {
        setIsCancelling(true);
        
        try {
            const token = localStorage.getItem("token");
            
            const response = await fetch('https://sawd.julesferry-ga.com/api/inventaires.php', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    action: 'annuler',
                    id: id
                })
            });
            
            const result = await response.json();
            
            if (result.success) {
                setShowCancelModal(false);
                fetchInventaire();
            } else {
                alert(result.message || "Erreur lors de l'annulation");
            }
        } catch (err) {
            alert("Erreur de connexion au serveur");
        } finally {
            setIsCancelling(false);
        }
    };

    const getStatutBadge = (statut) => {
        const configs = {
            brouillon: { 
                bg: 'warning', 
                label: 'Brouillon', 
                icon: 'ri-time-line',
                gradient: 'linear-gradient(135deg, #f59e0b, #d97706)'
            },
            valide: { 
                bg: 'success', 
                label: 'Validé', 
                icon: 'ri-check-double-line',
                gradient: 'linear-gradient(135deg, #10b981, #059669)'
            },
            annule: { 
                bg: 'danger', 
                label: 'Annulé', 
                icon: 'ri-close-circle-line',
                gradient: 'linear-gradient(135deg, #dc3545, #bb2d3b)'
            }
        };
        const config = configs[statut] || configs.brouillon;
        
        return (
            <Badge 
                className="px-4 py-3 rounded-pill fs-6 shadow-sm"
                style={{
                    background: config.gradient,
                    border: 'none',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                }}
            >
                <i className={`${config.icon} me-2`}></i>
                {config.label}
            </Badge>
        );
    };

    const calculerTauxPrecision = () => {
        if (!inventaire?.lignes) return 0;
        const produitsSansEcart = inventaire.lignes.filter(l => l.ecart === 0).length;
        return Math.round((produitsSansEcart / inventaire.lignes.length) * 100) || 0;
    };

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                        <div className="text-center">
                            <Spinner animation="border" variant="primary" className="mb-3" style={{ width: '3rem', height: '3rem' }} />
                            <p className="text-muted">Chargement de l'inventaire...</p>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    if (error || !inventaire) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="text-center py-5">
                        <i className="ri-error-warning-line fs-48 mb-3 d-block"></i>
                        <h4>{error || "Inventaire non trouvé"}</h4>
                        <Button variant="outline-danger" className="mt-3" onClick={() => navigate('/stock/inventaire')}>
                            <i className="ri-arrow-left-line me-2"></i>Retour à la liste
                        </Button>
                    </Alert>
                </div>
            </>
        );
    }

    const tauxPrecision = calculerTauxPrecision();

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="container-fluid">
                    {/* Fil d'Ariane */}
                    <Row className="mb-4">
                        <Col md={12}>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <Button 
                                    variant="link" 
                                    className="p-0 text-muted" 
                                    onClick={() => navigate('/stock/inventaire')}
                                >
                                    <i className="ri-arrow-left-line me-1"></i>Inventaires
                                </Button>
                                <span className="text-muted">/</span>
                                <span className="text-primary">Détail inventaire</span>
                            </div>
                        </Col>
                    </Row>

                    {/* En-tête moderne avec carte profil */}
                    <Row className="mb-4 fade-in">
                        <Col md={12}>
                            <Card className="border-0 shadow-lg overflow-hidden">
                                <div className="bg-gradient-primary" style={{ 
                                    height: '120px', 
                                    background: 'linear-gradient(135deg, #4361ee 0%, #ededed 100%)',
                                    position: 'relative'
                                }}>
                                    <div style={{
                                        position: 'absolute',
                                        top: '-20px',
                                        right: '20px',
                                        width: '200px',
                                        height: '200px',
                                        background: 'rgba(255, 255, 255, 0.1)',
                                        borderRadius: '50%',
                                        pointerEvents: 'none'
                                    }}></div>
                                    <div style={{
                                        position: 'absolute',
                                        bottom: '-30px',
                                        left: '30%',
                                        width: '150px',
                                        height: '150px',
                                        background: 'rgba(255, 255, 255, 0.05)',
                                        borderRadius: '50%',
                                        pointerEvents: 'none'
                                    }}></div>
                                </div>
                                <Card.Body className="position-relative" style={{ marginTop: '-60px' }}>
                                    <Row>
                                        <Col md={8}>
                                            <div className="d-flex align-items-end gap-4">
                                                <div className="bg-white rounded-4 shadow-lg p-2" style={{
                                                    boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)'
                                                }}>
                                                    <div className="bg-light rounded-3 d-flex align-items-center justify-content-center" style={{ 
                                                        width: '100px', 
                                                        height: '100px',
                                                        background: 'linear-gradient(135deg, #f8f9fa, #e9ecef)'
                                                    }}>
                                                        <i className="ri-clipboard-check-line" style={{ fontSize: '48px', color: '#4361ee' }}></i>
                                                    </div>
                                                </div>
                                                <div className="mb-2">
                                                    <h1 className="display-6 fw-bold mb-1" style={{ color: '#1e293b' }}>
                                                        {inventaire.reference_inventaire}
                                                    </h1>
                                                    <div className="d-flex align-items-center gap-3 flex-wrap">
                                                        <div className="d-flex align-items-center">
                                                            <i className="ri-calendar-line text-muted me-2"></i>
                                                            <span className="text-muted">
                                                                {formatDate(inventaire.date_inventaire)}
                                                            </span>
                                                        </div>
                                                        <div className="d-flex align-items-center">
                                                            <i className="ri-user-line text-muted me-2"></i>
                                                            <span className="text-muted">
                                                                {inventaire.prenom_utilisateur} {inventaire.nom_utilisateur}
                                                            </span>
                                                        </div>
                                                        {getStatutBadge(inventaire.statut)}
                                                    </div>
                                                </div>
                                            </div>
                                        </Col>
                                        <Col md={4}>
                                            <div className="d-flex justify-content-end gap-2 mt-3 mt-md-0">
                                                <Button 
                                                    variant="outline-secondary" 
                                                    onClick={() => navigate('/stock/inventaire')}
                                                    className="rounded-pill px-4 d-flex align-items-center gap-2"
                                                >
                                                    <i className="ri-arrow-left-line"></i>
                                                    <span>Retour</span>
                                                </Button>
                                                {inventaire.statut === 'brouillon' && (
                                                    <>
                                                        <Button 
                                                            variant="success" 
                                                            onClick={() => setShowValidateModal(true)}
                                                            className="rounded-pill px-4 d-flex align-items-center gap-2"
                                                            style={{
                                                                background: 'linear-gradient(135deg, #10b981, #059669)',
                                                                border: 'none'
                                                            }}
                                                        >
                                                            <i className="ri-check-double-line"></i>
                                                            <span>Valider</span>
                                                        </Button>
                                                        <Button 
                                                            variant="outline-danger" 
                                                            onClick={() => setShowCancelModal(true)}
                                                            className="rounded-pill px-4 d-flex align-items-center gap-2"
                                                        >
                                                            <i className="ri-close-circle-line"></i>
                                                            <span>Annuler</span>
                                                        </Button>
                                                    </>
                                                )}
                                                {inventaire.statut === 'valide' && (
                                                    <Badge 
                                                        className="px-4 py-3 rounded-pill fs-6"
                                                        style={{
                                                            background: 'linear-gradient(135deg, #10b981, #059669)',
                                                            border: 'none'
                                                        }}
                                                    >
                                                        <i className="ri-check-double-line me-2"></i>
                                                        Inventaire validé
                                                    </Badge>
                                                )}
                                            </div>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Cartes de statistiques */}
                    <Row className="g-4 mb-4 stats-row">
                        <Col md={3}>
                            <Card className="border-0 shadow-sm h-100 stat-card">
                                <Card.Body className="p-3 d-flex align-items-center">
                                    <div className="bg-primary bg-opacity-10 p-3 rounded-3 me-3">
                                        <i className="ri-stack-line text-primary fs-4"></i>
                                    </div>
                                    <div>
                                        <small className="text-muted d-block">Produits comptés</small>
                                        <h4 className="mb-0 fw-bold">{inventaire.lignes?.length || 0}</h4>
                                        <small className="text-muted">articles</small>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card className="border-0 shadow-sm h-100 stat-card">
                                <Card.Body className="p-3 d-flex align-items-center">
                                    <div className={`bg-${inventaire.total_ecart > 0 ? 'success' : inventaire.total_ecart < 0 ? 'danger' : 'secondary'} bg-opacity-10 p-3 rounded-3 me-3`}>
                                        <i className={`ri-${inventaire.total_ecart > 0 ? 'arrow-up' : inventaire.total_ecart < 0 ? 'arrow-down' : 'equal'}-line text-${inventaire.total_ecart > 0 ? 'success' : inventaire.total_ecart < 0 ? 'danger' : 'secondary'}`}></i>
                                    </div>
                                    <div>
                                        <small className="text-muted d-block">Écart total</small>
                                        <h4 className={`mb-0 fw-bold text-${inventaire.total_ecart > 0 ? 'success' : inventaire.total_ecart < 0 ? 'danger' : 'secondary'}`}>
                                            {inventaire.total_ecart > 0 ? '+' : ''}{inventaire.total_ecart || 0}
                                        </h4>
                                        <small className="text-muted">unités</small>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card className="border-0 shadow-sm h-100 stat-card">
                                <Card.Body className="p-3 d-flex align-items-center">
                                    <div className="bg-warning bg-opacity-10 p-3 rounded-3 me-3">
                                        <i className="ri-money-euro-circle-line text-warning fs-4"></i>
                                    </div>
                                    <div>
                                        <small className="text-muted d-block">Valeur écart</small>
                                        <h4 className="mb-0 fw-bold text-primary">{formatCurrency(inventaire.valeur_ecart || 0)}</h4>
                                        <small className="text-muted">FCFA</small>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card className="border-0 shadow-sm h-100 stat-card">
                                <Card.Body className="p-3 d-flex align-items-center">
                                    <div className="bg-info bg-opacity-10 p-3 rounded-3 me-3">
                                        <i className="ri-percent-line text-info fs-4"></i>
                                    </div>
                                    <div>
                                        <small className="text-muted d-block">Précision</small>
                                        <h4 className="mb-0 fw-bold text-info">{tauxPrecision}%</h4>
                                        <OverlayTrigger
                                            placement="top"
                                            overlay={<Tooltip>Produits sans écart / Total produits</Tooltip>}
                                        >
                                            <ProgressBar 
                                                now={tauxPrecision} 
                                                variant="info"
                                                style={{ height: '4px', width: '100px', marginTop: '4px' }}
                                            />
                                        </OverlayTrigger>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Commentaire */}
                    {inventaire.commentaire && (
                        <Card className="border-0 shadow-sm mb-4">
                            <Card.Body className="p-4">
                                <div className="d-flex">
                                    <div className="bg-light rounded-circle p-3 me-3">
                                        <i className="ri-chat-1-line text-primary fs-4"></i>
                                    </div>
                                    <div>
                                        <h6 className="mb-2">Commentaire</h6>
                                        <p className="mb-0 text-muted">{inventaire.commentaire}</p>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Détail des lignes */}
                    <Card className="border-0 shadow-lg table-card">
                        <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">
                                <i className="ri-list-check-2 me-2 text-primary"></i>
                                Détail de l'inventaire
                            </h5>
                            <Badge bg="primary" className="px-3 py-2 rounded-pill">
                                {inventaire.lignes?.length || 0} produits
                            </Badge>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="mb-0 align-middle custom-table">
                                    <thead className="bg-light">
                                        <tr>
                                            <th className="py-3 ps-4">Réf.</th>
                                            <th className="py-3">Produit</th>
                                            <th className="py-3">Catégorie</th>
                                            <th className="py-3 text-center">Théorique</th>
                                            <th className="py-3 text-center">Réel</th>
                                            <th className="py-3 text-center">Écart</th>
                                            <th className="py-3 text-end pe-4">Valeur écart</th>
                                            <th className="py-3">Commentaire</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {inventaire.lignes?.map((ligne, index) => (
                                            <tr key={ligne.id_ligne_inventaire} style={{
                                                animation: `fadeIn 0.3s ease ${index * 0.03}s both`,
                                                backgroundColor: ligne.ecart > 0 ? 'rgba(16, 185, 129, 0.03)' : 
                                                               ligne.ecart < 0 ? 'rgba(220, 53, 69, 0.03)' : ''
                                            }}>
                                                <td className="ps-4">
                                                    <Badge bg="light" text="dark" className="px-3 py-2 font-mono">
                                                        {ligne.reference}
                                                    </Badge>
                                                </td>
                                                <td>
                                                    <Link to={`/stock/produits/${ligne.id_produit}`} className="text-primary fw-semibold text-decoration-none">
                                                        {ligne.designation}
                                                    </Link>
                                                </td>
                                                <td>{ligne.nom_categorie || <span className="text-muted">-</span>}</td>
                                                <td className="text-center fw-bold">{ligne.quantite_theorique}</td>
                                                <td className="text-center fw-bold">{ligne.quantite_reelle}</td>
                                                <td className="text-center">
                                                    {ligne.ecart !== 0 && (
                                                        <Badge 
                                                            bg={ligne.ecart > 0 ? 'success' : 'danger'}
                                                            className="px-3 py-2 rounded-pill"
                                                            style={{
                                                                background: ligne.ecart > 0 
                                                                    ? 'linear-gradient(135deg, #10b981, #059669)' 
                                                                    : 'linear-gradient(135deg, #dc3545, #bb2d3b)',
                                                                border: 'none'
                                                            }}
                                                        >
                                                            <i className={`ri-${ligne.ecart > 0 ? 'arrow-up' : 'arrow-down'}-line me-1`}></i>
                                                            {ligne.ecart > 0 ? '+' : ''}{ligne.ecart}
                                                        </Badge>
                                                    )}
                                                </td>
                                                <td className="text-end pe-4">
                                                    <span className={`fw-bold ${ligne.ecart !== 0 ? 'text-primary' : 'text-muted'}`}>
                                                        {formatCurrency(ligne.ecart * ligne.prix_achat_ht)}
                                                    </span>
                                                </td>
                                                <td>
                                                    {ligne.commentaire ? (
                                                        <OverlayTrigger
                                                            placement="top"
                                                            overlay={<Tooltip>{ligne.commentaire}</Tooltip>}
                                                        >
                                                            <span className="text-truncate d-inline-block" style={{ maxWidth: '150px' }}>
                                                                <i className="ri-chat-1-line text-muted me-1"></i>
                                                                {ligne.commentaire}
                                                            </span>
                                                        </OverlayTrigger>
                                                    ) : (
                                                        <span className="text-muted">-</span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>
                </div>

                {/* Modal de validation amélioré */}
                <Modal 
                    show={showValidateModal} 
                    onHide={() => setShowValidateModal(false)} 
                    centered 
                    className="validate-modal"
                >
                    <Modal.Header closeButton className="border-0 pb-0">
                        <Modal.Title className="w-100">
                            <div className="text-center">
                                <div className="confirm-icon mb-3">
                                    <div className="icon-wrapper bg-success-soft">
                                        <i className="ri-check-double-line text-success"></i>
                                    </div>
                                </div>
                                <h3 className="fw-bold text-success">Valider l'inventaire</h3>
                            </div>
                        </Modal.Title>
                    </Modal.Header>
                    
                    <Modal.Body className="text-center pt-2">
                        <p className="text-muted mb-4">
                            Êtes-vous sûr de vouloir valider l'inventaire <strong>{inventaire?.reference_inventaire}</strong> ?
                        </p>
                        
                        <Alert variant="warning" className="text-start mb-4">
                            <div className="d-flex">
                                <i className="ri-alert-line fs-4 me-3 flex-shrink-0"></i>
                                <div>
                                    <strong>Attention !</strong>
                                    <p className="mb-0 small mt-1">
                                        La validation appliquera les écarts au stock en temps réel.
                                        Cette action est irréversible.
                                    </p>
                                </div>
                            </div>
                        </Alert>

                        <div className="bg-light p-4 rounded-4 mb-3">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <span className="text-muted">Produits avec écart :</span>
                                <Badge 
                                    bg="warning" 
                                    className="px-3 py-2 rounded-pill"
                                    style={{
                                        background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                                        border: 'none'
                                    }}
                                >
                                    {inventaire?.lignes?.filter(l => l.ecart !== 0).length}
                                </Badge>
                            </div>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <span className="text-muted">Écart total :</span>
                                <span className={`fw-bold fs-5 ${inventaire?.total_ecart > 0 ? 'text-success' : inventaire?.total_ecart < 0 ? 'text-danger' : ''}`}>
                                    {inventaire?.total_ecart > 0 ? '+' : ''}{inventaire?.total_ecart || 0}
                                </span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center">
                                <span className="text-muted">Valeur totale des écarts :</span>
                                <span className="fw-bold fs-5 text-primary">{formatCurrency(inventaire?.valeur_ecart || 0)}</span>
                            </div>
                        </div>
                    </Modal.Body>
                    
                    <Modal.Footer className="border-0 pt-0 justify-content-center">
                        <Button 
                            variant="outline-secondary" 
                            onClick={() => setShowValidateModal(false)}
                            className="px-4 rounded-pill"
                            disabled={isValidating}
                        >
                            <i className="ri-close-line me-2"></i>
                            Annuler
                        </Button>
                        <Button 
                            variant="success" 
                            onClick={handleValider}
                            className="px-5 rounded-pill"
                            disabled={isValidating}
                            style={{
                                background: 'linear-gradient(135deg, #10b981, #059669)',
                                border: 'none'
                            }}
                        >
                            {isValidating ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    Validation...
                                </>
                            ) : (
                                <>
                                    <i className="ri-check-double-line me-2"></i>
                                    Confirmer la validation
                                </>
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Modal d'annulation */}
                <Modal 
                    show={showCancelModal} 
                    onHide={() => setShowCancelModal(false)} 
                    centered 
                    className="cancel-modal"
                >
                    <Modal.Header closeButton className="border-0 pb-0">
                        <Modal.Title className="w-100">
                            <div className="text-center">
                                <div className="confirm-icon mb-3">
                                    <div className="icon-wrapper bg-danger-soft">
                                        <i className="ri-close-circle-line text-danger"></i>
                                    </div>
                                </div>
                                <h3 className="fw-bold text-danger">Annuler l'inventaire</h3>
                            </div>
                        </Modal.Title>
                    </Modal.Header>
                    
                    <Modal.Body className="text-center pt-2">
                        <p className="text-muted mb-4">
                            Êtes-vous sûr de vouloir annuler l'inventaire <strong>{inventaire?.reference_inventaire}</strong> ?
                        </p>
                        
                        <Alert variant="danger" className="text-start mb-4">
                            <div className="d-flex">
                                <i className="ri-alert-line fs-4 me-3 flex-shrink-0"></i>
                                <div>
                                    <strong>Cette action est irréversible</strong>
                                    <p className="mb-0 small mt-1">
                                        L'annulation supprimera définitivement cet inventaire.
                                    </p>
                                </div>
                            </div>
                        </Alert>
                    </Modal.Body>
                    
                    <Modal.Footer className="border-0 pt-0 justify-content-center">
                        <Button 
                            variant="outline-secondary" 
                            onClick={() => setShowCancelModal(false)}
                            className="px-4 rounded-pill"
                            disabled={isCancelling}
                        >
                            <i className="ri-close-line me-2"></i>
                            Non, conserver
                        </Button>
                        <Button 
                            variant="danger" 
                            onClick={handleAnnuler}
                            className="px-5 rounded-pill"
                            disabled={isCancelling}
                            style={{
                                background: 'linear-gradient(135deg, #dc3545, #bb2d3b)',
                                border: 'none'
                            }}
                        >
                            {isCancelling ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    Annulation...
                                </>
                            ) : (
                                <>
                                    <i className="ri-close-circle-line me-2"></i>
                                    Oui, annuler
                                </>
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>

                <style>{`
                    @keyframes fadeIn {
                        from { opacity: 0; transform: translateY(10px); }
                        to { opacity: 1; transform: translateY(0); }
                    }

                    .fade-in {
                        animation: fadeIn 0.5s ease;
                    }

                    .stats-row .stat-card {
                        transition: all 0.3s ease;
                        border-radius: 16px;
                    }

                    .stats-row .stat-card:hover {
                        transform: translateY(-5px);
                        box-shadow: 0 15px 30px rgba(0,0,0,0.1) !important;
                    }

                    .table-card {
                        border-radius: 16px;
                        overflow: hidden;
                    }

                    .custom-table thead th {
                        background: #f8fafc;
                        color: #1e293b;
                        font-weight: 600;
                        font-size: 0.85rem;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                        border-bottom: 2px solid #e2e8f0;
                        padding: 1rem 0.75rem;
                    }

                    .custom-table tbody td {
                        padding: 1rem 0.75rem;
                        vertical-align: middle;
                    }

                    .custom-table tbody tr {
                        transition: all 0.2s ease;
                    }

                    .custom-table tbody tr:hover {
                        background: #f1f5f9 !important;
                    }

                    .font-mono {
                        font-family: 'SF Mono', Monaco, 'Cascadia Code', monospace;
                    }

                    .validate-modal .modal-content,
                    .cancel-modal .modal-content {
                        border: none;
                        border-radius: 24px;
                        box-shadow: 0 20px 40px rgba(0,0,0,0.2);
                        overflow: hidden;
                    }

                    .validate-modal .modal-header {
                        background: linear-gradient(135deg, #f0fdf4, #ffffff);
                        border-bottom: none;
                        padding: 2rem 1.5rem 0.5rem;
                    }

                    .cancel-modal .modal-header {
                        background: linear-gradient(135deg, #fff5f5, #ffffff);
                        border-bottom: none;
                        padding: 2rem 1.5rem 0.5rem;
                    }

                    .validate-modal .modal-body,
                    .cancel-modal .modal-body {
                        padding: 1rem 2rem;
                    }

                    .validate-modal .modal-footer,
                    .cancel-modal .modal-footer {
                        border-top: 1px solid #e2e8f0;
                        padding: 1.5rem 2rem 2rem;
                        background: #f8fafc;
                    }

                    .icon-wrapper {
                        width: 80px;
                        height: 80px;
                        margin: 0 auto;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        border-radius: 50%;
                        font-size: 40px;
                        animation: pulse 2s infinite;
                    }

                    .bg-success-soft {
                        background: rgba(16, 185, 129, 0.15);
                    }

                    .bg-danger-soft {
                        background: rgba(220, 53, 69, 0.15);
                    }

                    .confirm-icon {
                        animation: bounceIn 0.5s ease-out;
                    }

                    @keyframes bounceIn {
                        0% { transform: scale(0); opacity: 0; }
                        50% { transform: scale(1.1); }
                        100% { transform: scale(1); opacity: 1; }
                    }

                    @keyframes pulse {
                        0% { transform: scale(1); }
                        50% { transform: scale(1.05); }
                        100% { transform: scale(1); }
                    }

                    .validate-modal .btn,
                    .cancel-modal .btn {
                        border-radius: 12px;
                        font-weight: 600;
                        padding: 0.6rem 1.5rem;
                        transition: all 0.3s ease;
                    }

                    .validate-modal .btn:hover:not(:disabled),
                    .cancel-modal .btn:hover:not(:disabled) {
                        transform: translateY(-2px);
                        box-shadow: 0 8px 16px rgba(0,0,0,0.1);
                    }

                    .validate-modal .alert,
                    .cancel-modal .alert {
                        border: none;
                        border-radius: 12px;
                        border-left: 4px solid;
                    }

                    .validate-modal .alert {
                        background: linear-gradient(135deg, #fff3cd, #fff9e6);
                        border-left-color: #ffc107;
                    }

                    .cancel-modal .alert {
                        background: linear-gradient(135deg, #f8d7da, #fdf0f2);
                        border-left-color: #dc3545;
                    }
                `}</style>

                <Footer />
            </div>
        </>
    );
}